import React, { useState } from "react";
import axios from "axios";
import { useApp, API } from "../state/AppContext";
import { Input } from "./ui/input";
import { Download, Lock, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const UNLOCK_KEY = "scl_guide_unlocked";
const PDF_KEY = "scl_guide_pdf";

export function useGuideUnlock() {
  const [unlocked, setUnlockedState] = useState(() => localStorage.getItem(UNLOCK_KEY) === "1");
  const [pdfUrl, setPdfUrl] = useState(() => localStorage.getItem(PDF_KEY) || "");
  const markUnlocked = (url) => {
    localStorage.setItem(UNLOCK_KEY, "1");
    if (url) { localStorage.setItem(PDF_KEY, url); setPdfUrl(url); }
    setUnlockedState(true);
  };
  return { unlocked, pdfUrl, markUnlocked };
}

export function GuideUnlockGate({ activity, onUnlocked }) {
  const { t, lang, sourcePath, utmContent, utmSource, utmCampaign, utmMedium } = useApp();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email)) { toast.error(t("unlock_email_invalid")); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/guide-leads`, {
        email: email.trim().toLowerCase(),
        phone: phone.trim() || null,
        source_path: sourcePath || "spinner",
        language: lang || "en",
        utm_content: utmContent || "",
        utm_source: utmSource || "",
        utm_campaign: utmCampaign || "",
        utm_medium: utmMedium || "",
        trigger_activity_id: activity?.id,
        trigger_activity_name: activity?.name,
      });
      toast.success(t("unlock_success"));
      onUnlocked(res.data.pdf_url);
    } catch {
      toast.error("Something went wrong — try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-5" data-testid="unlock-gate">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl bg-scl-teal/10 flex items-center justify-center flex-shrink-0">
          <Lock className="w-5 h-5 text-scl-teal" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-scl-teal font-semibold mb-1">{t("unlock_kicker")}</div>
          <h3 className="font-serif text-lg sm:text-xl text-scl-charcoal font-bold leading-snug">{t("unlock_headline")}</h3>
        </div>
      </div>

      <label className="text-[11px] uppercase tracking-widest text-scl-charcoal/60 font-semibold">{t("unlock_email")} <span className="text-scl-brick">*</span></label>
      <div className="relative mt-1.5 mb-3">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-scl-charcoal/40" />
        <Input
          data-testid="unlock-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="pl-9 h-12 rounded-xl border-scl-border bg-white text-base"
          required
        />
      </div>

      <label className="text-[11px] uppercase tracking-widest text-scl-charcoal/60 font-semibold">{t("unlock_phone")} <span className="text-scl-charcoal/40 normal-case tracking-normal">— {t("optional")}</span></label>
      <div className="relative mt-1.5 mb-1">
        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-scl-charcoal/40" />
        <Input
          data-testid="unlock-phone"
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="(317) 555-0123"
          className="pl-9 h-12 rounded-xl border-scl-border bg-white text-base"
        />
      </div>
      <p className="mb-4 text-[11px] text-scl-charcoal/55">{t("unlock_phone_help")}</p>

      <button
        type="submit"
        disabled={submitting}
        data-testid="unlock-submit"
        className="w-full h-12 rounded-full bg-scl-teal text-white font-semibold text-[15px] shadow-warm-sm hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-70 flex items-center justify-center gap-2 min-h-[44px]"
      >
        <Download className="w-4 h-4" />
        {submitting ? t("unlock_sending") : t("unlock_button")}
      </button>
    </form>
  );
}

export function GuideDownloadCard({ pdfUrl }) {
  const { t } = useApp();
  return (
    <a
      href={pdfUrl}
      target="_blank"
      rel="noreferrer"
      download
      data-testid="guide-download"
      className="mt-4 flex items-center gap-3 p-4 rounded-2xl bg-scl-mustard/30 border border-scl-mustard/60 hover:bg-scl-mustard/50 active:scale-[0.99] transition-all min-h-[44px]"
    >
      <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
        <Download className="w-5 h-5 text-scl-teal" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-serif text-[15px] font-bold text-scl-charcoal leading-tight">{t("guide_download")}</div>
        <div className="text-[11px] text-scl-charcoal/65">{t("guide_note")}</div>
      </div>
    </a>
  );
}
